import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { useStore } from "react-redux";
import { flushPendingSaves, type PendingSaves } from "../../../common/input/pendingSaves";
import { useAppDispatch } from "../../../common/redux/hooks";
import type { RootState } from "../../../common/redux/store";
import { editContractThunk } from "../../../common/redux/thunks/contract/edit-contract-thunk";

export type SaveBeforeLeaving = {
  leave: (go: () => void) => Promise<void>;
  isSaving: boolean;
  saveFailed: boolean;
  valueRefused: boolean;
};

const SaveBeforeLeavingContext = createContext<SaveBeforeLeaving | null>(null);

export const useSaveBeforeLeavingState = (registry: PendingSaves): SaveBeforeLeaving => {
  const store = useStore<RootState>();
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [valueRefused, setValueRefused] = useState(false);

  const leave = useCallback(
    async (go: () => void) => {
      setIsSaving(true);
      setSaveFailed(false);
      setValueRefused(false);
      let saved = true;
      try {
        await flushPendingSaves(registry);
        for (const failed of store.getState().contract.state?.failedEdits ?? []) {
          await dispatch(editContractThunk({ command: failed.command }));
        }
      } catch {
        saved = false;
      } finally {
        setIsSaving(false);
      }
      const state = store.getState().contract.state;
      if (saved && (state?.errors.length ?? 0) > 0) saved = false;
      if (saved) {
        go();
        return;
      }
      setSaveFailed(true);
      setValueRefused((state?.failedEdits ?? []).some((failed) => failed.valueRefused));
    },
    [registry, store, dispatch],
  );

  return { leave, isSaving, saveFailed, valueRefused };
};

export const SaveBeforeLeavingProvider = ({ value, children }: { value: SaveBeforeLeaving; children: ReactNode }) => (
  <SaveBeforeLeavingContext.Provider value={value}>{children}</SaveBeforeLeavingContext.Provider>
);

export const useSaveBeforeLeaving = (): SaveBeforeLeaving | null => useContext(SaveBeforeLeavingContext);
